import type { InventoryVehicle } from '@carvana/merch-contracts';
import TrackWrapper from '@/common/TrackWrapper';
import VehicleTile from '@/srp/VehicleTile';
import ConditionalTileType from '~/constants/srp/ConditionalTile';
import { useRequestIds } from '~/context/srp/InventoryContext';
import InventoryConditionalTile from '~/contracts/srp/InventoryConditionalTile';
import { logError } from '~/utilities/loggers';
import getTileWrapperProps from '~/utilities/srp/getTileWrapperProps';
import DownPaymentTile from './ConditionalTiles/DownPaymentTile';
import FinanceTile from './ConditionalTiles/FinanceTile';
import GreatDealTile from './ConditionalTiles/GreatDealTile';
import SaveSearchTile from './ConditionalTiles/SaveSearchTile';
import SebastianTileWrapper from './ConditionalTiles/SebastianTile/SebastianTileWrapper';
import TradeInTile from './ConditionalTiles/TradeInTile';

// Type guards for different tile types
const isVehicle = (
  tile: InventoryVehicle | InventoryConditionalTile
): tile is InventoryVehicle => {
  return 'vehicleId' in tile;
};

const isInventoryConditionalTile = (
  tile: InventoryVehicle | InventoryConditionalTile
): tile is InventoryConditionalTile => {
  return 'key' in tile && 'position' in tile;
};

const createInventoryConditionalTileComponent = (
  key: ConditionalTileType,
  position: number
) => {
  switch (key) {
    case ConditionalTileType.FinanceExpiredPrequal:
    case ConditionalTileType.FinanceNoPrequal:
      return <FinanceTile key="finance-tile" position={position} />;
    case ConditionalTileType.FinanceZeroMinDown:
      return (
        <DownPaymentTile
          key="finance-tile"
          tileToShow="zero"
          position={position}
        />
      );
    case ConditionalTileType.FinanceLowMinDown:
      return (
        <DownPaymentTile
          key="finance-tile"
          tileToShow="low"
          position={position}
        />
      );
    case ConditionalTileType.GreatDeals:
      return <GreatDealTile key="great-deal-tile" position={position} />;
    case ConditionalTileType.SavedSearch:
      return <SaveSearchTile key="save-search-tile" position={position} />;
    case ConditionalTileType.SebastianTileA:
    case ConditionalTileType.SebastianTileB:
      return <SebastianTileWrapper key={key} name={key} position={position} />;
    case ConditionalTileType.TradeNoTrade:
      return <TradeInTile key="trade-in-tile" position={position} />;
    default:
      return null;
  }
};

type OwnProps = {
  tiles: Array<InventoryVehicle | InventoryConditionalTile>;
  shouldCalculateEvWidthInventory?: boolean;
};

export default function TileRenderer({ tiles }: OwnProps) {
  const requestIds = useRequestIds();

  return (
    <>
      {tiles.map((tile, index) => {
        if (isVehicle(tile)) {
          const wrapperProps = getTileWrapperProps({
            index,
            isConditionalTile: false,
            isSimilarVehicle: false,
            totalTiles: tiles.length,
            ...requestIds
          });

          return (
            <TrackWrapper key={tile.vehicleId} {...wrapperProps}>
              <VehicleTile
                highPriorityImage={index < 4}
                vehicle={{ ...tile, isSimilarTile: false }}
              />
            </TrackWrapper>
          );
        }

        if (isInventoryConditionalTile(tile)) {
          const wrapperProps = getTileWrapperProps({
            index,
            isConditionalTile: true,
            isSimilarVehicle: false,
            totalTiles: tiles.length,
            ...requestIds
          });

          return (
            <TrackWrapper
              key={`conditional-${tile.key}-${tile.position}`}
              {...wrapperProps}
            >
              {createInventoryConditionalTileComponent(tile.key, tile.position)}
            </TrackWrapper>
          );
        }

        logError('No tile component found for tile', { tile });

        return null;
      })}
    </>
  );
}
